import * as React from "react";
import { UitkCarousel } from "uitk-react-carousel";
import { HotelCard } from "../components/HotelCard";
import { inject } from "mobx-react";
import { ViewCarouselProps } from "../typings";
import { Hotel } from "typings/microserviceModels/hotels-flex-module";
import { AspectRatioEnum } from "stores/thumbor/ThumborStore";
import { AnalyticsConfig } from "uitk-react-utils";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedTrackEvent
} from "components/utility/analytics/FlexAnalyticsUtils";
import { ViewAllHotelsButton } from "../components/ViewAllHotelsButton";
import { EstimatedPriceLabel } from "../components/EstimatedPriceLabel";
import { CardContent } from "../components/CardContent";
import { Viewport, ViewSmall, ViewLarge } from "bernie-view";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";

@inject("hotels", "thumbor", "analytics")
export class ViewCarousel extends React.Component<ViewCarouselProps> {
  // This should be removed after UITK Carousel is fixed. (https://jira.expedia.biz/browse/UDS-577)
  private safelyResizeImage(hotel: Hotel) {
    const image: string = hotel.hotelImageUrls && hotel.hotelImageUrls.y;

    return image && this.props.thumbor
      ? this.props.thumbor.safelyGenerateThumborizedImageUrl(
          image,
          340,
          150,
          this.props.context,
          AspectRatioEnum.DEFAULT,
          true,
          100
        )
      : image;
  }

  public render() {
    const flexComponent = this.props.flexComponent;
    const keyHelper = new ItemKeyHelper("hotels");
    const model = flexComponent && flexComponent.model;
    const l10n = this.props.l10n;
    const context = this.props.context;

    if (!flexComponent || !model || !model.hotels || model.hotels.length === 0) {
      return null;
    }

    const cardContent = (hotel: Hotel) => () => (
      <CardContent
        model={model}
        hotel={hotel}
        context={this.props.context}
        locale={this.props.context.locale}
        isViewCarousel={true}
      />
    );

    const hotelCardProps = (hotel: Hotel) => ({
      callToActionHotelName: model.callToActionHotelName,
      context,
      l10n,
      className: "all-r-margin-three",
      hotel,
      CardContent: cardContent(hotel),
      isMobile: true
    });

    // Since uitk-carousel is only client-side, we need to generate the thumbor images server-side here
    model.hotels.forEach(hotel => this.safelyResizeImage(hotel));

    // To be replaced by a more generic hotel card carousel
    const HotelCardCarousel = () => {
      const carouselProps = {
        itemsVisible: { lg: 1, md: 1, sm: 1 },
        pageBy: 1,
        peekWidth: 24,
        buttonText: {
          nextButton: l10n.formatText("carousel.item.next"),
          prevButton: l10n.formatText("carousel.item.prev")
        }
      };

      const analytics: AnalyticsConfig = {
        id: "TG.LP.Dest.Hotels",
        callback: (id, description) => {
          const flexTrackingInfo: FlexTrackingInfo = {
            moduleName: id,
            action: Action.SCROLL,
            linkName: description
          };
          sendDelayedTrackEvent(flexTrackingInfo, this.props.analytics);
        }
      };

      return (
        <>
          <UitkCarousel
            analytics={analytics}
            carouselName={"Hotels"}
            {...carouselProps}
            headline={model.title}
          >
            {model.hotels.map((hotel, hotelIndex) => (
              <HotelCard
                {...hotelCardProps(hotel)}
                key={keyHelper.next()}
                className="uitk-carousel-item"
                index={hotelIndex + 1}
              />
            ))}
          </UitkCarousel>
          <EstimatedPriceLabel l10n={l10n} className="all-t-padding-two" />
        </>
      );
    };

    return (
      <Viewport>
        <ViewSmall>
          <div
            className="Hotels Hotels_ViewCarousel uitk-cell all-b-margin-six"
            id={flexComponent.id}
            data-fm={flexComponent.fmId}
          >
            <HotelCardCarousel />
            <ViewAllHotelsButton l10n={l10n} model={model} />
          </div>
        </ViewSmall>
        <ViewLarge>
          <div
            className="Hotels uitk-cell all-cell-1-1 all-b-margin-six"
            id={flexComponent.id}
            data-fm={flexComponent.fmId}
          >
            <h3 className="uitk-type-heading-600">{model.title}</h3>
            <EstimatedPriceLabel l10n={l10n} />
            <div className="uitk-grid all-cell-1-3 all-x-gutter-three">
              {model.hotels.map((hotel, hotelIndex) => (
                <div
                  className="HotelsCard uitk-cell all-b-padding-three all-b-margin-three"
                  key={keyHelper.next()}
                >
                  <HotelCard {...hotelCardProps(hotel)} index={hotelIndex + 1} />
                </div>
              ))}
            </div>
            <ViewAllHotelsButton l10n={l10n} model={model} />
          </div>
        </ViewLarge>
      </Viewport>
    );
  }
}

export default ViewCarousel;
